import { createMetrics } from './metrics';
import { buildSnapshot } from './snapshot';
import { startVisitSignalsClock } from '../adapters/visitSignals';
import { sendVisitSignals } from '../../services/visitSignals';

const FIRST_SEND_MS = 20000;
const MIN_INTERVAL_MS = 60000;

/**
 * When a behaviour snapshot is sent.
 *
 * The client who leaves without pressing anything is the common outcome and has no other moment at
 * which anything would be sent, so the leave trigger is the one that must never be missed. The timer
 * is insurance for a tab the system kills without warning, and it stays quiet while nothing is
 * happening: a visit nobody touched sends exactly once, on the way out.
 *
 * Snapshots are cumulative and numbered, so a lost one costs nothing and the order of arrival does
 * not matter — the store keeps the highest number it has seen.
 */
export const initBehaviour = (uuid: string | undefined): (() => void) => {
  const metrics = createMetrics();
  const detachMetrics = metrics.attach(document);
  startVisitSignalsClock(Date.now());

  let seq = 0;
  let lastSentAt = 0;
  let stopped = false;

  const send = (): void => {
    if (!uuid || stopped) return;
    seq += 1;
    lastSentAt = Date.now();
    const snapshot = buildSnapshot(metrics.read(), seq);
    metrics.markClean();
    // Not awaited on purpose: the send never rejects, and on the way out there is no tick left to
    // await in.
    void sendVisitSignals(uuid, snapshot);
  };

  const tick = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (!metrics.dirty()) return;
    if (lastSentAt !== 0 && Date.now() - lastSentAt < MIN_INTERVAL_MS) return;
    send();
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') send();
  };

  const first = setTimeout(tick, FIRST_SEND_MS);
  const timer = setInterval(tick, MIN_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    stopped = true;
    clearTimeout(first);
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    detachMetrics();
  };
};
